<script lang="ts">
	import { run } from 'svelte/legacy';

	import { createSwitch, melt } from '@melt-ui/svelte';
	import IconMoon from '$lib/assets/svg/icons/moon.svg?component';
	import IconSun from '$lib/assets/svg/icons/sun.svg?component';
	import newUniqueId from 'locally-unique-id-generator';

	interface Props {
		label?: string;
		labelChecked?: string;
		variant?: 'label' | 'icon' | 'theme';
		name?: string;
		checked?: boolean;
		disabled?: boolean;
		testid?: string | null;
		icon?: import('svelte').Snippet;
		iconChecked?: import('svelte').Snippet;
		onChange?: (value: boolean) => void | boolean;
	}

	let {
		label = '',
		labelChecked = '',
		variant = $bindable('label'),
		name = '',
		checked = $bindable(false),
		disabled = false,
		testid = null,
		icon,
		iconChecked,
		onChange
	}: Props = $props();

	const {
		elements: { root, input },
		states: { checked: internalChecked },
		options
	} = createSwitch({
		name,
		value: checked ? labelChecked : label,
		defaultChecked: checked,
		disabled
	});

	let internalValue = $derived($internalChecked ? labelChecked : label);
	// Update internal checked state when checked prop changes
	run(() => {
		internalChecked.set(checked);
	});

	// Dispatch change event when internal checked state changes
	run(() => {
		if ($internalChecked !== checked) {
			onChange?.($internalChecked);
		}
	});

	run(() => {
		if (name !== undefined) {
			options.name.set(name);
			// options.value.set(value);
		}
	});

	run(() => {
		options.value.set(internalValue);
	});

	run(() => {
		if (icon || iconChecked) {
			variant = 'icon';
		}
	});

	const uid = `Switch-${newUniqueId()}`;
</script>

<div class="Switch Switch--{variant}">
	<label class="Switch__label" for="Switch-{uid}" id="Switch-label-{uid}">
		{label}
	</label>
	<button
		class="Switch__button"
		data-testid={testid}
		use:melt={$root}
		id="Switch-{uid}"
		aria-labelledby="Switch-label-{uid}"
	>
		<span class="Switch__options">
			{#if variant === 'label'}
				<span data-testid={label} class="Switch__option">{label}</span>
				<span data-testid={labelChecked} class="Switch__option">{labelChecked}</span>
			{/if}
			{#if variant === 'icon'}
				<span class="Switch__option Switch__option--icon">{@render icon?.()}</span>
				<span class="Switch__option Switch__option--icon">{@render iconChecked?.()}</span>
			{/if}
			{#if variant === 'theme'}
				<span class="Switch__option"><IconMoon></IconMoon></span>
				<span class="Switch__option"><IconSun></IconSun></span>
			{/if}
		</span>
		<span class="Switch__thumb"></span>
	</button>
	<input use:melt={$input} />
</div>

<style lang="scss" src="./Switch.scss"></style>
